import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useStores } from "src/stores";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { useEffect, useState, useRef } from "react";
import { useServices } from "src/services";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar
} from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button, ToolbarSpacer } from "@progress/kendo-react-buttons";
import { useHistory } from "react-router";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { D4URxEvent } from "src/utils/classes";
import {
  ColumnMenu,
  ColumnMenuCheckboxFilter,
} from "src/components/GridColumnMenus";
import multiColumnSort from "multi-column-sort";
import moment from 'moment-timezone';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { savePDF } from '@progress/kendo-react-pdf';
import html2canvas from 'html2canvas';

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
  headerCellOptions?: object;
}

const EventsScreen = () => {
  // Add this useEffect to inject the CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .k-dialog .k-window-titlebar {
        background-color: #007bff !important;
        border-color: #007bff !important;
      }
      .k-dialog .k-window-title {
        color: white !important;
      }
      .k-dialog .k-window-titlebar .k-window-action {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const localizationService = useLocalization();

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const _exportRDA = useRef<ExcelExport | null>(null);

  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'event_request_id', field: 'event_request_id', title: 'Request ID' },
    { key: 'event_id', field: 'event_id', title: 'Latitude Event ID' },
    { key: 'ffs_event_id', field: 'ffs_event_id', title: 'FFS Event ID' },
    { key: 'first_time_dealer', field: 'first_time_dealer', title: '1st Time Dealer' },
    { key: 'hispanic', field: 'hispanic', title: 'Hispanic Dealer' },
    { key: 'dealer_name', field: 'dealer_name', title: 'Dealer' },
    { key: 'dealer_email', field: 'dealer_email', title: 'Dealer Email' },
    { key: 'dealer_secondary_email', field: 'dealer_secondary_email', title: 'Dealer Secondary Email' },
    { key: 'dealer_tertiary_email', field: 'dealer_tertiary_email', title: 'Dealer Tertiary Email' },
    { key: 'parts_code', field: 'parts_code', title: 'Sales Code' },
    { key: 'pa_code', field: 'pa_code', title: 'PA Code' },
    { key: 'region', field: 'region', title: 'Dealer Region' },
    { key: 'market_area', field: 'market_area', title: 'Dealer Market' },
    { key: 'charity_partner', field: 'charity_partner', title: 'Organization' },
    { key: 'event_city', field: 'event_city', title: 'Event City' },
    { key: 'event_state', field: 'event_state', title: 'Event State' },
    { key: 'event_zip', field: 'event_zip', title: 'Event Zip' },
    { key: 'funding_purpose', field: 'funding_purpose', title: 'Funding Purpose' },
    { key: 'funding_purpose_category', field: 'funding_purpose_category', title: 'Funding Purpose Category' },
    { key: 'outreach_done', field: 'outreach_done', title: 'Outreach Done', headerCellOptions: { textAlign: 'center' } },
    { key: 'outreach_notes_raw', field: 'outreach_notes_raw', title: 'Outreach Notes', headerCellOptions: { textAlign: 'center' } },
    { key: 'start_date', field: 'start_date', title: 'Event Date' },
    { key: 'status', field: 'status', title: 'Status' },
    { key: 'check_amount', field: 'check_amount', title: 'Check Amount', headerCellOptions: { textAlign: 'center' } },
    { key: 'pre_registrant_count', field: 'pre_registrant_count', title: 'Pre-Event Registrations', headerCellOptions: { textAlign: 'center' } },
    { key: 'total_pre_drives', field: 'total_pre_drives', title: 'Pre-Drives (Total)', headerCellOptions: { textAlign: 'center' } },
    { key: 'total_post_drives', field: 'total_post_drives', title: 'Post-Drive Surveys', headerCellOptions: { textAlign: 'center' } },
    { key: 'valid_post_count', field: 'valid_post_count', title: 'Valid Post-Drive Surveys', headerCellOptions: { textAlign: 'center' } },
    { key: 'event_location_type', field: 'event_location_type', title: 'Event Location Type' },
  ];

  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );

  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAllExportColumns = () => setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  const clearAllExportColumns = () => setSelectedExportColumns([]);

  const selectedExportColumnDefs = exportColumnDefs.filter((col) =>
    selectedExportColumns.includes(col.key)
  );
  
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const singleMonthCalendarRef = useRef<HTMLDivElement | null>(null);
  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [showIndividualMonthDialog, setShowIndividualMonthDialog] = useState(false);
  const [selectedMonthForExport, setSelectedMonthForExport] = useState<number>(0); // 0 = April, 8 = December

  const CALENDAR_MONTHS = ['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const exportCalendarImage = () => {
    if (!calendarRef.current) return;
    html2canvas(calendarRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then((canvas) => {
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `events_calendar_${moment().format('YYYYMMDD')}.png`;
      link.click();
    });
  };

  const exportSingleMonthPDF = () => {
    if (singleMonthCalendarRef.current) {
      const monthName = CALENDAR_MONTHS[selectedMonthForExport];
      savePDF(singleMonthCalendarRef.current, {
        paperSize: 'A4',
        landscape: true,
        fileName: `events_calendar_2026_${monthName}.pdf`,
        margin: { top: 10, left: 10, right: 10, bottom: 10 },
        scale: 0.6
      });
    }
  };
  // const excelExportRDA = () => {
  //   if (_exportRDA.current !== null) {
  //     _exportRDA.current.save();
  //   }
  // };

  const { ui, login } = useStores();
  const { d4ur } = useServices();
  let history = useHistory();

  const sortPresets: { [key: string]: State; } = {
    clear: {
      sort: [
        {
          field: 'sort_date',
          dir: 'asc',
        },
      ]
    },
    default: {
      filter: {
        logic: "and", filters: [
          {
            logic: "and", filters: [
              { field: "sort_date", operator: "gte", value: moment().toDate() },
            ]
          },
          {
            logic: "or", filters: [
              { field: "status_pretty", operator: "eq", value: "New" },
              { field: "status_pretty", operator: "eq", value: "Eligible" },
              { field: "status_pretty", operator: "eq", value: "Planning" },
              { field: "status_pretty", operator: "eq", value: "Revise" },
              { field: "status_pretty", operator: "eq", value: "Review" },
              { field: "status_pretty", operator: "eq", value: "Approved" },
              { field: "status_pretty", operator: "eq", value: "Event Ending" },
              { field: "status_pretty", operator: "eq", value: "Event Ended" },
              { field: "status_pretty", operator: "eq", value: "Donation Requested" },
              { field: "status_pretty", operator: "eq", value: "Donation Approved" },
              { field: "status_pretty", operator: "eq", value: "Check Approved" },
              { field: "status_pretty", operator: "eq", value: "Reimbursement Requested" },
              { field: "status_pretty", operator: "eq", value: "Reimbursement Approved" },
            ]
          }
        ]
      },
      sort: [
        {
          field: 'status_pretty',
          dir: 'asc',
        },
        {
          field: 'sort_date',
          dir: 'asc',
        },
      ]
    },
    action: {
      filter: {
        logic: "and", filters: [
          // {logic: "and", filters: [
          //     {field: "sort_date", operator: "gte", value: moment().toDate()},
          // ]},
          {
            logic: "or", filters: [
              { field: "status_pretty", operator: "eq", value: "New" },
              { field: "status_pretty", operator: "eq", value: "Review" },
              { field: "status_pretty", operator: "eq", value: "Reimbursement Submitted" },
              { field: "status_pretty", operator: "eq", value: "Donation Requested" },
              { field: "status_pretty", operator: "eq", value: "Event Ended" },
            ]
          }
        ]
      },
      sort: [
        {
          field: 'status_pretty',
          dir: 'asc',
        },
        {
          field: 'sort_date',
          dir: 'asc',
        },
      ]
    },
    waiting: {
      filter: {
        logic: "and", filters: [
          // {logic: "and", filters: [
          //     {field: "sort_date", operator: "gte", value: moment().toDate()},
          // ]},
          {
            logic: "or", filters: [
              { field: "status_pretty", operator: "eq", value: "Eligible" },
              { field: "status_pretty", operator: "eq", value: "Planning" },
              { field: "status_pretty", operator: "eq", value: "Revise" },
              { field: "status_pretty", operator: "eq", value: "Donation Approved" },
            ]
          }
        ]
      },
      sort: [
        {
          field: 'status_pretty',
          dir: 'asc',
        },
        {
          field: 'sort_date',
          dir: 'asc',
        },
      ]
    },
    yesterday: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "sort_date", operator: "eq", value: moment().subtract(1, 'days').startOf('day').toDate() },
          ]
        }]
      },
      sort: [
        {
          field: 'dealer_name',
          dir: 'asc',
        },
      ]
    },
    today: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "sort_date", operator: "eq", value: moment().startOf('day').toDate() },
          ]
        }]
      },
      sort: [
        {
          field: 'dealer_name',
          dir: 'asc',
        },
      ]
    },
    tomorrow: {
      filter: { logic: "and", filters: [{ logic: "and", filters: [{ field: "sort_date", operator: "eq", value: moment().add(1, 'days').startOf('day').toDate() }] }] },
      sort: [
        {
          field: 'dealer_name',
          dir: 'asc',
        },
      ]
    },
    thisWeek: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "sort_date", operator: "gte", value: moment().startOf('isoWeek').startOf('day').toDate() },
            { field: "sort_date", operator: "lte", value: moment().endOf('isoWeek').startOf('day').toDate() },
          ]
        }]
      },
      sort: [
        {
          field: 'sort_date',
          dir: 'asc',
        },
        {
          field: 'dealer_name',
          dir: 'asc',
        },
      ]
    },
    nextWeek: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "sort_date", operator: "gte", value: moment().add(1, 'weeks').startOf('isoWeek').startOf('day').toDate() },
            { field: "sort_date", operator: "lte", value: moment().add(1, 'weeks').endOf('isoWeek').startOf('day').toDate() },
          ]
        }]
      },
      sort: [
        {
          field: 'sort_date',
          dir: 'asc',
        },
        {
          field: 'dealer_name',
          dir: 'asc',
        },
      ]
    },
    lastWeek: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "sort_date", operator: "gte", value: moment().subtract(1, 'weeks').startOf('isoWeek').startOf('day').toDate() },
            { field: "sort_date", operator: "lte", value: moment().subtract(1, 'weeks').endOf('isoWeek').startOf('day').toDate() },
          ]
        }]
      },
      sort: [
        {
          field: 'sort_date',
          dir: 'asc',
        },
        {
          field: 'dealer_name',
          dir: 'asc',
        },
      ]
    },
    blueCruise: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "bonus_event", operator: "eq", value: 1 },
          ]
        }]
      },
      sort: [
        {
          field: 'sort_date',
          dir: 'asc',
        },
      ]
    },
    past: {
      filter: {
        logic: "and", filters: [
          {
            logic: "and", filters: [
              { logic: "and", filters: [{ field: "sort_date", operator: "lt", value: moment().toDate() }] },
            ]
          },
        ]
      },
      sort: [
        {
          field: 'status_pretty',
          dir: 'asc',
        },
        {
          field: 'sort_date',
          dir: 'desc',
        },
      ]
    },
  }

  const [dataState, setDataState] = useState<State>(sortPresets.default);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [emailSentDialogVisible, setEmailSentDialogVisible] = useState<boolean>(false);
  
  // Debug: Log message state changes
  useEffect(() => {
    if (errorMessage) console.log('DEBUG: errorMessage set to:', errorMessage);
  }, [errorMessage]);
  
  useEffect(() => {
    if (successMessage) console.log('DEBUG: successMessage set to:', successMessage);
  }, [successMessage]);

  // Add state for the outreach notes modal
  const [notesModalVisible, setNotesModalVisible] = useState<boolean>(false);
  const [selectedEventNotes, setSelectedEventNotes] = useState<OutreachNote[]>([]);
  const [selectedEventDealer, setSelectedEventDealer] = useState<string>('');

  // Add state for the add outreach note modal
  const [addNoteModalVisible, setAddNoteModalVisible] = useState<boolean>(false);
  const [selectedEventForNote, setSelectedEventForNote] = useState<number | null>(null);
  const [selectedEventDealerForNote, setSelectedEventDealerForNote] = useState<string>('');
  const [newNoteText, setNewNoteText] = useState<string>('');

  // Add state for adding note within the notes modal
  const [showAddNoteInModal, setShowAddNoteInModal] = useState<boolean>(false);
  const [newNoteTextInModal, setNewNoteTextInModal] = useState<string>('');

  // Add state to track the event ID for the notes modal
  const [selectedEventForNoteId, setSelectedEventForNoteId] = useState<number | null>(null);

  // Add new state for editing notes
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editedNoteText, setEditedNoteText] = useState<string>('');

  // Add state to store the original note content
  const [originalNoteContent, setOriginalNoteContent] = useState<string>('');




  type Direction = "ASC" | "DESC";
  type SortObject<T> = {
    [key in keyof T]?: Direction;
  };

  const customProcess = (data: any[], state: State) => {
    const preProcessed = process(data, state).data;
    // custom sort
    const getColumnValues = (column: any, value: any) => {
      switch (column) {
        // case 'sort_status':
        //     return value.data.value;
        default:
          return value;
      }
    }

    const sortOrder: SortObject<any> = {};

    for (const s of (state.sort || [])) {
      let fieldToSort = s.field;

      switch (fieldToSort) {
        case 'status_pretty':
          fieldToSort = 'sort_status';
          break;
      }

      sortOrder[fieldToSort] = s.dir?.toUpperCase() as Direction;
    };

    let newData = [];
    if (state.sort?.length) {
      newData = multiColumnSort(
        preProcessed,
        sortOrder,
        getColumnValues
      );
    } else {
      newData = preProcessed;
    }

    return {
      data: newData,
      total: preProcessed.length,
    } as DataResult;
  }


  const processEvents = (events: D4URxEvent[]): D4URxEvent[] => {
    return events
      // FILTER OUT BILL KNIGHT PILOT EVENTS - REMOVE AT END OF SEASON
      .filter((e) => e.event_id !== 13541 && e.event_id !== 13542)
      .map((e) => {
        return new D4URxEvent(e);
      });
  };

  // const [planners, setPlanners] = useState<UserDetails[]>([]);
  const [eventList, setEventList] = useState<D4URxEvent[]>([]);
  const [dataResult, setDataResult] = useState<DataResult>(
    customProcess(eventList, dataState)
  );
  const [rdaData, setRDAData] = useState<DataResult>();

  useEffect(() => {
    if (eventList.length === 0) return;
    const measureWidth = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) setScrollbarWidth(gridContent.scrollWidth);
    };
    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
    };
    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.addEventListener('scroll', syncTopScrollbar);
    });
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [eventList.length, dataResult.data.length]);

  const loadEvents = () => {
    ui.setIsGettingEvents(true);
    return d4ur.getEvents(ui.currentSeason.season_id).then((ret) => {
      ui.setIsGettingEvents(false);
      console.log('[Events] raw fetch result:', ret);

      const processedEvents = processEvents(
        (ret as D4URxFetchResult).result || []
      );
      console.log('[Events] processed events:', processedEvents);

      setEventList(processedEvents);
      setDataResult(customProcess(processedEvents, dataState));
      const rdaTemp = processedEvents.filter((e) => e.event_id && !e.cancelled);
      setRDAData({
        data: rdaTemp,
        total: rdaTemp.length,
      });
      
      return processedEvents; // Return the processed events
    });
  }

  useEffect(() => {
    // ui.setIsGettingEvents(true);
    // d4ur.getEvents(8).then((ret) => {
    //   // TODO: make season dynamic
    //   ui.setIsGettingEvents(false);

    //   const processedEvents = processEvents(
    //     (ret as D4URxFetchResult).result || []
    //   );

    //   setEventList(processedEvents);
    //   setDataResult(customProcess(processedEvents, dataState));
    //   const rdaTemp = processedEvents.filter((e) => e.event_id && !e.cancelled);
    //   setRDAData({
    //     data: rdaTemp,
    //     total: rdaTemp.length,
    //   });
    // });
    loadEvents();

    // d4ur.getPlanners().then((plannersResult) => {
    //   setPlanners((plannersResult as D4URxFetchResult).result as UserDetails[]);
    // })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (notesModalVisible || addNoteModalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [notesModalVisible, addNoteModalVisible]);



  const EventActionsCell = (props: GridCellProps) => {
    const { event_id, event_request_id } = new D4URxEvent(props.dataItem);

    return (
      <td>
        <Button
          className="k-button k-primary"
          // onClick={() => props.enterEdit(props.dataItem)}
          onClick={() => {
            if (event_id) {
              history.push(`/event/${event_id}`);
            } else {
              history.push(`/eventRequest/${event_request_id}`);
            }
          }}
        >
          View / Edit
        </Button>
      </td>
    );
  };

  const BlueCruiseCell = (props: GridCellProps) => {
    const { event_id, bonus_event } = new D4URxEvent(props.dataItem);
    return (
      <td>
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
          }}>
          <label>Blue Cruise Event</label>
          <input
            type="checkbox"
            checked={bonus_event}
            style={{ width: "30px", height: "30px", cursor: "pointer" }}
            onChange={(e) => {
              d4ur.setBlueCruiseStatus(event_id, e.target.checked).then((ret) => {
                if (ret.response.status === 200) {
                  setIsLoading(false);
                  loadEvents();
                } else {
                  setErrorMessage(ret.result.message)
                  setIsLoading(false);
                }
              })
                .catch(e => console.log('UPDATE EVENT ERROR', e))
            }}
          />
        </div>
      </td>
    );
  };

  const OutreachCell = (props: GridCellProps) => {
    const { event_id, outreach_done, outreach_notes, dealer_name } = new D4URxEvent(props.dataItem);
    const notesCount = outreach_notes ? outreach_notes.length : 0;
    
    const openNotesModal = () => {
      setSelectedEventNotes(outreach_notes || []);
      setSelectedEventDealer(dealer_name);
      setSelectedEventForNoteId(event_id!); // Add this line
      setShowAddNoteInModal(false); // Reset add note state
      setNewNoteTextInModal(''); // Reset text
      setNotesModalVisible(true);
    };

    const openAddNoteModal = () => {
      setSelectedEventForNote(event_id!);
      setSelectedEventDealerForNote(dealer_name);
      setNewNoteText('');
      setAddNoteModalVisible(true);
    };
    
    return (
      <td>
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
          }}>
        <label>Outreach Done</label>
        <input
          type="checkbox"
          checked={outreach_done !== null}
          style={{ width: "16px", height: "16px", cursor: "pointer" }}
          onChange={(e) => {
            d4ur.setOutreachStatus(event_id!, e.target.checked).then((ret) => {
              if (ret.response.status === 200) {
                setIsLoading(false);
                loadEvents();
              } else {
                setErrorMessage(ret.result.message)
                setIsLoading(false);
              }
            })
              .catch(e => console.log('UPDATE OUTREACH ERROR', e))
          }}
        />
        
        {/* Conditional rendering based on notes count */}
        {notesCount > 0 ? (
          <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
            <span style={{ fontSize: "12px" }}>Notes({notesCount})</span>
            <span
              className={'k-icon k-i-edit'}
              style={{ color: '#666', marginLeft: '4px', fontSize: '12px', cursor: 'pointer' }}
              onClick={openNotesModal}
            />
          </div>
        ) : (
          <Button
            className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
            style={{ 
              marginTop: "4px", 
              fontSize: "10px", 
              padding: "2px 8px",
              backgroundColor: "#007bff",
              borderColor: "#007bff"
            }}
            onClick={openAddNoteModal}
          >
            Add Note
          </Button>
        )}
      </div>
    </td>
  );
};

  // Function to handle saving new note
  const handleSaveNote = () => {
    if (!newNoteText.trim()) {
      setErrorMessage('Note text is required.');
      return;
    }

    if (selectedEventForNote && login.user?.credentials) {
      d4ur.addOutreachNote(
        selectedEventForNote, 
        newNoteText.trim(),
        login.user.credentials.email,  // or login.user.credentials.display_name
        login.user.credentials.display_name
      ).then((ret) => {
        if (ret.response.status === 200) {
          setAddNoteModalVisible(false);
          setNewNoteText('');
          setSelectedEventForNote(null);
          setSelectedEventDealerForNote('');
          loadEvents(); // Refresh the data
        } else {
          setErrorMessage(ret.result.message || 'Error adding note.');
        }
      })
      .catch(e => {
        console.log('ADD NOTE ERROR', e);
        setErrorMessage('Error adding note.');
      });
    }
  };

  // Function to handle saving new note from within the notes modal
  const handleSaveNoteInModal = () => {
    if (!newNoteTextInModal.trim()) {
      setErrorMessage('Note text is required.');
      return;
    }

    // Find the event_id from the selected notes (we can get it from the first note or pass it separately)
    // For now, let's add the event_id to our state when opening the modal
    if (selectedEventForNoteId && login.user?.credentials) {
      d4ur.addOutreachNote(
        selectedEventForNoteId, 
        newNoteTextInModal.trim(),
        login.user.credentials.email,
        login.user.credentials.display_name
      ).then((ret) => {
        if (ret.response.status === 200) {
          setNewNoteTextInModal('');
          setShowAddNoteInModal(false);
          
          // Wait for loadEvents to complete, then refresh modal data
          loadEvents().then((updatedEvents) => {
            const updatedEvent = updatedEvents.find(e => e.event_id === selectedEventForNoteId);
            if (updatedEvent) {
              setSelectedEventNotes(updatedEvent.outreach_notes || []);
            }
          });
          
        } else {
          setErrorMessage(ret.result.message || 'Error adding note.');
        }
      })
      .catch(e => {
        console.log('ADD NOTE ERROR', e);
        setErrorMessage('Error adding note.');
      });
    }
  };

  // Function to handle saving edited note
  const handleSaveEditedNote = (originalTimestamp: string) => {
    if (!editedNoteText.trim()) {
      return;
    }

    if (selectedEventForNoteId && login.user?.credentials) {
      d4ur.updateOutreachNote(
        selectedEventForNoteId, 
        originalTimestamp,
        originalNoteContent, // Pass the original note content
        editedNoteText.trim()
      ).then((ret) => {
        if (ret.response.status === 200) {
          setEditingNoteIndex(null);
          setEditedNoteText('');
          setOriginalNoteContent('');
          
          // Update local modal state immediately
          setSelectedEventNotes(prevNotes => 
            prevNotes.map((note, index) => 
              index === editingNoteIndex 
                ? { ...note, note: editedNoteText.trim() }
                : note
            )
          );
          
          // Also refresh the full data in background
          loadEvents().then((updatedEvents) => {
            const updatedEvent = updatedEvents.find(e => e.event_id === selectedEventForNoteId);
            if (updatedEvent) {
              setSelectedEventNotes(updatedEvent.outreach_notes || []);
            }
          });
        }
      })
      .catch(e => {
        console.log('UPDATE NOTE ERROR', e);
      });
    }
  };

  // Add this function after the other outreach functions in Events.tsx
  const handleDeleteOutreachNote = (originalTimestamp: string, noteContent: string, noteIndex: number) => {
    if (selectedEventForNoteId) {
      if (window.confirm('Are you sure you want to delete this note?')) {
        d4ur.deleteOutreachNote(
          selectedEventForNoteId,
          originalTimestamp,
          noteContent
        ).then((ret) => {
          if (ret.response.status === 200) {
            
            // Update local modal state immediately by removing the note
            setSelectedEventNotes(prevNotes => 
              prevNotes.filter((_, index) => index !== noteIndex)
            );
            
            // Also refresh the full data in background
            loadEvents().then((updatedEvents) => {
              const updatedEvent = updatedEvents.find(e => e.event_id === selectedEventForNoteId);
              if (updatedEvent) {
                setSelectedEventNotes(updatedEvent.outreach_notes || []);
              }
            });
            
          } else {
            setErrorMessage(ret.result.message || 'Error deleting note.');
          }
        })
        .catch(e => {
          console.log('DELETE OUTREACH NOTE ERROR', e);
          setErrorMessage('Error deleting note.');
        });
      }
    }
  };



  const DealerNameCell = (props: GridCellProps) => {
    const { dealer_name, dealer_notes, event_id, event_request_id } = new D4URxEvent(props.dataItem);
    return (
      <td>
        {dealer_name}
        {dealer_notes && dealer_notes.length > 0 && (<span
          key={dealer_name + 'notes'}
          className={'k-icon k-i-file-txt'}
          style={{ color: '#ffa600', margin: 10 }}
          onClick={() => {
            if (event_id) {
              history.push(`/event/${event_id}`);
            } else {
              history.push(`/eventRequest/${event_request_id}`);
            }
          }}
        />)}
      </td>
    );
  };

  const TrainingStatusCell = (props: GridCellProps) => {
    const sendReminder = () => {
      setIsLoading(true);
      d4ur.triggerEmail('pre_event', props.dataItem.event_id).then((ret: any) => {
        if (ret.response.status === 200) {
          setIsLoading(false);
          setEmailSentDialogVisible(true);
          // Auto-hide the notification after 3 seconds
          setTimeout(() => {
            setEmailSentDialogVisible(false);
          }, 3000);
        } else {
          setErrorMessage(ret.result.message)
          setIsLoading(false);
        }
      })
        .catch(e => {
          console.log('TRIGGER EMAIL ERROR', e);
          setIsLoading(false);
        })
    }
    const getLastSentAt = (emailsSent: Array<emailsSent>) => {
      const parseDate = (email: emailsSent) => new Date(email.sent_at);
      emailsSent.sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime());
      const mostRecentEmail = emailsSent[0];

      return `${moment(mostRecentEmail.sent_at).tz('America/New_York').format('MMMM Do, h:mm a')} (EST) to ${mostRecentEmail.sent_to.length > 1 ? mostRecentEmail.sent_to.join(', ') : mostRecentEmail.sent_to}`;
    }
    const trainingStatus: string = props.dataItem.training_status;
    const trainingStatusPretty: string = props.dataItem.training_status_pretty;
    if (trainingStatus === "due") {
      return (
        <td>
          <p style={{ fontSize: '14px' }}>{trainingStatusPretty}</p>
          <Button
            className="k-button k-primary"
            onClick={() => { sendReminder() }}
          >
            Send Reminder Email
          </Button>
        </td>
      )
    } else if (trainingStatus === "reminder sent") {
      return (
        <td>
          <p style={{ fontSize: '14px' }}>{trainingStatusPretty}</p>
          <p style={{ fontSize: '12px' }}>Last Reminder Sent At: {getLastSentAt(props.dataItem.formatted_custom_data.training_reminder_sent)}</p>
          <Button
            className="k-button k-primary"
            onClick={() => { sendReminder() }}
          >
            Re-send Reminder
          </Button>
        </td>
      )
    } else if (trainingStatus === "completed") {

      return (
        <td>
          <p style={{ fontSize: '14px' }}>Completed ({props.dataItem.training_completed_by})</p>
        </td>
      )
    } else {
      return (
        <td>
          <p style={{ fontSize: '14px' }}>{trainingStatusPretty}</p>
        </td>
      )
    }
  };

  const doDataState = (state: State) => {
    setDataResult(customProcess(eventList, state));
    setDataState(state);
  }

  const rowRender = (row, props) => {
    // if blue cruise event, make row blue
    let color = "#fff";
    let fontColor = 'black';
    if (props.dataItem.bonus_event) {
      color = "#00095B";
      fontColor = 'white';
    }
    let style = {
      ...row.props.style,
      backgroundColor: color,
      borderColor: "black",
      borderWidth: 2,
      color: fontColor,
    };
    return <tr {...row.props} style={style}></tr>;
  };

  // const plannersCell = (props: GridCellProps) => {
  //   const { display_name, eligibility_custom_data } = new D4URxEvent(props.dataItem);
  //   const assignedPlannerID = JSON.parse(eligibility_custom_data || '').assigned_planner
  //   if (display_name) {
  //     return (
  //       <td>
  //         <p>{display_name}</p>
  //       </td>
  //     )
  //   }
  //   else if (assignedPlannerID && planners.length) {
  //     return (
  //       <td>
  //         <p>{planners?.find((x) => x.user_id === assignedPlannerID)?.display_name}</p>
  //       </td>
  //     )
  //   } else {
  //     return (
  //       <td>
  //       </td>
  //     )
  //   }
  // };


  const approvedStatuses = ['approved', 'event ended', 'event ending', 'in progress', 'donation requested', 'donation approved', 'reimbursement submitted', 'reimbursement requested', 'reimbursement approved'];
  // Exclude specific events from stats cards only (still shown in grid)
  const EVENT_IDS_EXCLUDED_FROM_STATS = [13884, 13885];
  const eventListForStats = eventList.filter((e) => !EVENT_IDS_EXCLUDED_FROM_STATS.includes(e.event_id!));

  // Function to generate event address components based on location type
  const generateEventAddressComponents = (event: any) => {
    try {
      const location = event.event_custom_data ? JSON.parse(event.event_custom_data).event_location_type : '';
      
      if (location === "The School" || location === "The Organization") {
        return {
          city: event.charity_city || '',
          state: event.charity_state || '',
          zip: event.charity_postal_code || ''
        };
      } else if (location === "The Dealership") {
        return {
          city: event.dealer_city || '',
          state: event.dealer_state || '',
          zip: event.dealer_zip || ''
        };
      } else {
        return {
          city: event.city || '',
          state: event.state || '',
          zip: event.zip_code || ''
        };
      }
    } catch (e) {
      console.error('Error generating event address components:', e);
      return { city: '', state: '', zip: '' };
    }
  };



  const editExportData = (data: any) => {
  return data.map((d: any) => {

    
    let formattedNotes = '';
    let formattedOutreachNotes = '';
    let formattedDealerNotes = '';
    
    if (d.dealer_notes && d.dealer_notes.length) {
      try {
        const cleanedNotes = typeof d.dealer_notes === 'string' ? d.dealer_notes.trim() : d.dealer_notes;
        if (typeof cleanedNotes === 'string' && (cleanedNotes === '' || cleanedNotes === 'null' || cleanedNotes === 'undefined')) {
          formattedNotes = '';
        } else if (typeof cleanedNotes === 'string' && (cleanedNotes.startsWith('[') || cleanedNotes.startsWith('{'))) {
          const notes = JSON.parse(cleanedNotes);
          if (notes.length) {
            formattedNotes = notes
              .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
              .map((note: any) => {
                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
              })
              .join('\n');
          }
        } else if (Array.isArray(cleanedNotes)) {
          // Already parsed array
          if (cleanedNotes.length) {
            formattedNotes = cleanedNotes
              .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
              .map((note: any) => {
                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
              })
              .join('\n');
          }
        }
      } catch (e) {
        console.error('Error parsing dealer notes:', e);
        formattedNotes = '';
      }
    }

    // Format dealer notes for export (using dealer_notes_raw)
    if (d.dealer_notes_raw && d.dealer_notes_raw.length) {
      try {
        const cleanedDealerNotes = typeof d.dealer_notes_raw === 'string' ? d.dealer_notes_raw.trim() : d.dealer_notes_raw;
        if (typeof cleanedDealerNotes === 'string' && (cleanedDealerNotes === '' || cleanedDealerNotes === 'null' || cleanedDealerNotes === 'undefined')) {
          formattedDealerNotes = '';
        } else if (typeof cleanedDealerNotes === 'string' && (cleanedDealerNotes.startsWith('[') || cleanedDealerNotes.startsWith('{'))) {
          const dealerNotes = JSON.parse(cleanedDealerNotes);
          if (dealerNotes.length) {
            formattedDealerNotes = dealerNotes
              .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
              .map((note: any) => {
                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
              })
              .join('\n');
          }
        } else if (Array.isArray(cleanedDealerNotes)) {
          // Already parsed array
          if (cleanedDealerNotes.length) {
            formattedDealerNotes = cleanedDealerNotes
              .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
              .map((note: any) => {
                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
              })
              .join('\n');
          }
        }
      } catch (e) {
        console.error('Error parsing dealer notes for export:', e);
        formattedDealerNotes = '';
      }
    }

    // Format outreach notes for export
    if (d.outreach_notes_raw && d.outreach_notes_raw.length) {
      try {
        const outreachNotes = JSON.parse(d.outreach_notes_raw);
        if (outreachNotes.length) {
          formattedOutreachNotes = outreachNotes
            .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
            .map((note: any) => {
              const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
              return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
            })
            .join('\n');
        }
      } catch (e) {
        console.error('Error parsing outreach notes:', e);
        formattedOutreachNotes = '';
      }
    }

    const getSpreadsheetValue = (field: string) => {
      try {
        const spreadsheetData = JSON.parse(d.spreadsheet_data || '{}');
        return spreadsheetData[field] || d[field];
      } catch (e) {
        return d[field];
      }
    };

    // Get address components
    const addressComponents = generateEventAddressComponents(d);

    let eventLocationType = '';
    try {
      eventLocationType = d.event_custom_data ? JSON.parse(d.event_custom_data).event_location_type || '' : '';
    } catch (e) {
      console.error('Error parsing event_custom_data for location type:', e);
    }

    const result = {
      ...d,
      event_location_type: eventLocationType,
      dealer_notes: formattedNotes,
      dealer_notes_raw: formattedDealerNotes,
      outreach_notes_raw: formattedOutreachNotes,
      event_city: addressComponents.city,
      event_state: addressComponents.state,
      event_zip: addressComponents.zip,
      funding_purpose: getSpreadsheetValue('funding_purpose'),
      funding_purpose_category: getSpreadsheetValue('funding_purpose_category'),
      bonus_event: d.bonus_event ? 'YES' : null,
      first_time_dealer: d.first_time_dealer ? 'YES' : null,
      hispanic: d.hispanic ? 'YES' : null,
    };
    return result;
  });
  }


  return (
    <div id="Dashboard" className="dashboard-page main-content">
      {/* Email Sent Success Notification */}
      {emailSentDialogVisible && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#28a745',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span style={{ fontSize: '18px' }}>✓</span>
          <span>Email sent successfully</span>
        </div>
      )}
      {/* Add Note Modal */}
      {addNoteModalVisible && (
        <Dialog
          title={`Add Outreach Note - ${selectedEventDealerForNote}`}
          onClose={() => {
            setAddNoteModalVisible(false);
            setNewNoteText('');
            setSelectedEventForNote(null);
            setSelectedEventDealerForNote('');
          }}
          width={500}
          height={300}
        >
          <div style={{ padding: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Note:
            </label>
            <textarea
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Enter your outreach note here..."
              style={{
                width: "calc(100% - 20px)", // Subtract padding to create space
                height: "120px",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                resize: "vertical",
                marginRight: "10px" // Additional right margin
              }}
            />
          </div>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setAddNoteModalVisible(false);
                setNewNoteText('');
                setSelectedEventForNote(null);
                setSelectedEventDealerForNote('');
              }}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              style={{
                backgroundColor: "#007bff",
                borderColor: "#007bff",
                color: "white"
              }}
              onClick={handleSaveNote}
              disabled={!newNoteText.trim()}
            >
              Save Note
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Updated Notes Modal */}
      {notesModalVisible && (
        <Dialog
          title={`Outreach Notes - ${selectedEventDealer}`}
          onClose={() => {
            setNotesModalVisible(false);
            setShowAddNoteInModal(false);
            setNewNoteTextInModal('');
            setSelectedEventForNoteId(null);
          }}
          width={600}
          height={showAddNoteInModal ? 550 : 400}
        >
          <div style={{ padding: "20px" }}>
            {/* Add Note Section - Moved to top */}
            {showAddNoteInModal ? (
              <div style={{ 
                marginBottom: "20px", 
                padding: "15px", 
                border: "1px solid #007bff", 
                borderRadius: "5px",
                backgroundColor: "#f8f9fa"
              }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                  Add New Note:
                </label>
                <textarea
                  value={newNoteTextInModal}
                  onChange={(e) => setNewNoteTextInModal(e.target.value)}
                  placeholder="Enter your outreach note here..."
                  style={{
                    width: "100%",
                    height: "80px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                    boxSizing: "border-box"
                  }}
                />
                <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                    style={{
                      backgroundColor: "#007bff",
                      borderColor: "#007bff",
                      color: "white"
                    }}
                    onClick={handleSaveNoteInModal}
                    disabled={!newNoteTextInModal.trim()}
                  >
                    Save Note
                  </Button>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                    onClick={() => {
                      setShowAddNoteInModal(false);
                      setNewNoteTextInModal('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                style={{ 
                  marginBottom: "20px",
                  backgroundColor: "#007bff",
                  borderColor: "#007bff"
                }}
                onClick={() => setShowAddNoteInModal(true)}
              >
                Add Note
              </Button>
            )}

            {selectedEventNotes.length > 0 ? (
              selectedEventNotes.map((note, index) => (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: "20px", 
                    padding: "15px", 
                    border: "1px solid #ddd", 
                    borderRadius: "5px",
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  {editingNoteIndex === index ? (
                    // Edit mode - stack everything vertically
                    <div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Note:</strong>
                        <textarea
                          value={editedNoteText}
                          onChange={(e) => setEditedNoteText(e.target.value)}
                          style={{
                            width: "100%",
                            height: "80px",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            marginTop: "5px",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                        <span style={{ marginRight: "15px" }}>
                          <strong>Created:</strong> {note.created_at}
                        </span>
                        <span>
                          <strong>By:</strong> {note.created_by}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                          style={{
                            backgroundColor: "#007bff",
                            borderColor: "#007bff",
                            color: "white"
                          }}
                          onClick={() => handleSaveEditedNote(note.created_at)}
                          disabled={!editedNoteText.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                          onClick={() => {
                            setEditingNoteIndex(null);
                            setEditedNoteText('');
                            setOriginalNoteContent('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode - keep original layout
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Note:</strong>
                          <p style={{ margin: "5px 0", lineHeight: "1.4" }}>{note.note}</p>
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          <span style={{ marginRight: "15px" }}>
                            <strong>Created:</strong> {note.created_at}
                          </span>
                          <span>
                            <strong>By:</strong> {note.created_by}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginLeft: "10px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            style={{
                              backgroundColor: "#007bff",
                              borderColor: "#007bff",
                              color: "white"
                            }}
                            onClick={() => {
                              setEditingNoteIndex(index);
                              setEditedNoteText(note.note);
                              setOriginalNoteContent(note.note); // Store original content
                            }}
                          >
                            Edit
                          </Button>
                          <span
                            className={'k-icon k-i-trash'}
                            style={{ 
                              color: '#dc3545', 
                              fontSize: '16px', 
                              cursor: 'pointer',
                              padding: '8px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            onClick={() => handleDeleteOutreachNote(note.created_at, note.note, index)}
                            title="Delete note"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No notes available.</p>
            )}
          </div>
          <DialogActionsBar>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setNotesModalVisible(false);
                setShowAddNoteInModal(false);
                setNewNoteTextInModal('');
                setSelectedEventForNoteId(null);
              }}
            >
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}



      <div className="card-container grid">
        <h3 className="card-title">
          {login.user?.credentials.display_name}:{" "}
          {localizationService.toLanguageString("custom.events", "events")}
          <div className="event-stats-container" style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
            {console.log('eventList:', eventList)}
            <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>Event Requests</h4>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Total:</span>
                <strong>{eventListForStats.length}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Approved:</span>
                <strong>{eventListForStats.filter(event => approvedStatuses.includes(event.status as D4URxEventStatus)).length}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>New:</span>
                <strong>{eventListForStats.filter(event => event.status === "new").length}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Ineligible:</span>
                <strong>{eventListForStats.filter(event => event.status === "ineligible").length}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Cancelled:</span>
                <strong>{eventListForStats.filter(event => event.status === "cancelled").length}</strong>
              </div>
            </div>
            <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>First Time Dealers</h4>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Requests:</span>
                <strong>{eventListForStats.filter(event => event.first_time_dealer && !event.hispanic).length}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Approved:</span>
                <strong>{eventListForStats.filter(event => event.first_time_dealer && !event.hispanic && approvedStatuses.includes(event.status as D4URxEventStatus)).length}</strong>
              </div>
            </div>
            <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>Hispanic Dealers</h4>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Requests:</span>
                <strong>{eventListForStats.filter(event => event.hispanic).length}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Approved:</span>
                <strong>{eventListForStats.filter(event => event.hispanic && approvedStatuses.includes(event.status as D4URxEventStatus)).length}</strong>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", marginLeft: "20px", gap: "10px" }}>
              <Button
                onClick={() => setShowCalendarDialog(true)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
              >
                Calendar Export (All)
              </Button>
              <Button
                onClick={() => setShowIndividualMonthDialog(true)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
              >
                Individual Month Export
              </Button>
            </div>
          </div>
          
          {/* National Charity Partners - Wider Box */}
          {/* <div style={{ marginTop: "10px" }}>
            <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", width: "400px" }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>National Charity Partners</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div style={{ display: "flex" }}>
                  <span>Feeding America: </span>
                  <strong style={{ marginLeft: "8px" }}>{eventList.filter(event => event.nat_charity === "Feeding America").length}</strong>
                </div>
                <div style={{ display: "flex" }}>
                  <span>Team Rubicon: </span>
                  <strong style={{ marginLeft: "8px" }}>{eventList.filter(event => event.nat_charity === "Team Rubicon").length}</strong>
                </div>
                <div style={{ display: "flex" }}>
                  <span>Habitat for Humanity: </span>
                  <strong style={{ marginLeft: "8px" }}>{eventList.filter(event => event.nat_charity === "Habitat for Humanity").length}</strong>
                </div>
                <div style={{ display: "flex" }}>
                  <span>American Red Cross: </span>
                  <strong style={{ marginLeft: "8px" }}>{eventList.filter(event => event.nat_charity === "American Red Cross").length}</strong>
                </div>
                <div style={{ display: "flex" }}>
                  <span>June Campaign: </span>
                  <strong style={{ marginLeft: "8px" }}>{eventList.filter(event => event.nat_charity === "June Campaign").length}</strong>
                </div>
              </div>
            </div>
          </div> */}
        </h3>
        {/* // TODO: going to replace "sending..." with real spinner during styling updates. same on Event page.  */}
        {isLoading ? <p style={{ color: 'blue' }}>Sending...</p> : null}
        {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : null}
        {successMessage ? <p style={{ color: 'green' }}>{successMessage}</p> : null}
        <div className="card-component">
          <ExcelExport data={editExportData(dataResult.data.filter((e: any) => !EVENT_IDS_EXCLUDED_FROM_STATS.includes(e.event_id)))} ref={_export} fileName={`events_export_${moment().format('YYYYMMDD-hhmma')}.xlsx`}>
            {selectedExportColumnDefs.map((col) => (
              <ExcelExportColumn
                key={col.key}
                field={col.field}
                title={col.title}
                {...(col.headerCellOptions ? { headerCellOptions: col.headerCellOptions } : {})}
              />
            ))}
          </ExcelExport>

          {/* Calendar Export Dialog */}
          {showCalendarDialog && (
            <Dialog
              title="Events Calendar - April to December 2026"
              onClose={() => setShowCalendarDialog(false)}
              width={1200}
              height={700}
            >
              <div style={{ padding: '10px', overflowY: 'auto', maxHeight: '550px' }}>
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <Button
                    onClick={exportCalendarImage}
                    style={{
                      padding: "10px 30px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Download Image (PNG)
                  </Button>
                </div>
                
                <div ref={calendarRef} style={{ backgroundColor: 'white', padding: '20px' }}>
                  {['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((monthName, monthIndex) => {
                    const monthNumber = monthIndex + 4;
                    const daysInMonth = moment(`2026-${monthNumber}`, 'YYYY-M').daysInMonth();
                    const firstDayOfWeek = moment(`2026-${monthNumber}-01`, 'YYYY-M-DD').day();
                    
                    const monthEvents = eventList.filter(event => {
                      if (EVENT_IDS_EXCLUDED_FROM_STATS.includes(event.event_id as number)) return false;
                      if (!event.start_date) return false;
                      if (!approvedStatuses.includes(event.status as D4URxEventStatus)) return false;
                      const eventDate = moment(event.start_date);
                      return eventDate.year() === 2026 && eventDate.month() === monthNumber - 1;
                    });

                    const eventsByDay: { [key: number]: D4URxEvent[] } = {};
                    monthEvents.forEach(event => {
                      const day = moment(event.start_date).date();
                      if (!eventsByDay[day]) eventsByDay[day] = [];
                      eventsByDay[day].push(event);
                    });

                    // Helper function to get event location (matching Event.tsx logic)
                    const getEventLocation = (event: D4URxEvent) => {
                      const fmt = (city?: string, state?: string) => {
                        const c = city?.trim() || '';
                        const s = state?.trim() || '';
                        if (!c && !s) return '';
                        if (!c) return s;
                        if (!s) return c;
                        return `${c}, ${s}`;
                      };
                      const location = event.event_custom_data ? JSON.parse(event.event_custom_data).event_location_type : '';
                      if (location === "The School" || location === "The Organization") {
                        return fmt(event.charity_city, event.charity_state) || fmt(event.city, event.state) || fmt(event.dealer_city, event.dealer_state);
                      } else if (location === "The Dealership") {
                        return fmt(event.dealer_city, event.dealer_state) || fmt(event.city, event.state) || fmt(event.charity_city, event.charity_state);
                      }
                      // No event_custom_data (e.g. older events) — try all sources in order
                      return fmt(event.city, event.state) || fmt(event.dealer_city, event.dealer_state) || fmt(event.charity_city, event.charity_state);
                    };

                    return (
                      <div key={monthName} style={{ marginBottom: '40px' }}>
                        <h2 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>
                          {monthName} 2026
                        </h2>
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse', 
                          border: '2px solid #333',
                          tableLayout: 'fixed'
                        }}>
                          <thead>
                            <tr>
                              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                                <th key={day} style={{ 
                                  border: '1px solid #666',
                                  padding: '10px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}>
                                  {day}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: Math.ceil((daysInMonth + firstDayOfWeek) / 7) }).map((_, weekIndex) => (
                              <tr key={weekIndex}>
                                {Array.from({ length: 7 }).map((_, dayIndex) => {
                                  const dayNumber = weekIndex * 7 + dayIndex - firstDayOfWeek + 1;
                                  const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
                                  const dayEvents = isValidDay ? eventsByDay[dayNumber] || [] : [];

                                  return (
                                    <td key={dayIndex} style={{ 
                                      border: '1px solid #999',
                                      padding: '8px',
                                      verticalAlign: 'top',
                                      height: '100px',
                                      backgroundColor: isValidDay ? 'white' : '#f0f0f0',
                                      position: 'relative'
                                    }}>
                                      {isValidDay && (
                                        <>
                                          <div style={{ 
                                            fontWeight: 'bold', 
                                            marginBottom: '6px', 
                                            fontSize: '16px',
                                            color: '#333'
                                          }}>
                                            {dayNumber}
                                          </div>
                                          {dayEvents.map((event, idx) => (
                                            <div key={idx} style={{ 
                                              marginBottom: '4px',
                                              padding: '4px',
                                              backgroundColor: '#e3f2fd',
                                              borderLeft: '3px solid #007bff',
                                              fontSize: '11px',
                                              lineHeight: '1.3'
                                            }}>
                                              <div style={{ fontWeight: '600', color: '#000' }}>
                                                {event.event_name}
                                              </div>
                                              <div style={{ color: '#555' }}>
                                                {getEventLocation(event)}
                                              </div>
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </div>
              <DialogActionsBar>
                <Button onClick={() => setShowCalendarDialog(false)}>
                  Close
                </Button>
              </DialogActionsBar>
            </Dialog>
          )}

          {/* Individual Month Export Dialog */}
          {showIndividualMonthDialog && (
            <Dialog
              title="Export Single Month"
              onClose={() => setShowIndividualMonthDialog(false)}
              width={900}
              height={600}
            >
              <div style={{ padding: '10px', overflowY: 'auto' }}>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontWeight: 'bold' }}>Month:</label>
                  <DropDownList
                    data={CALENDAR_MONTHS.map((name, i) => ({ label: name, value: i }))}
                    dataItemKey="value"
                    textField="label"
                    value={{ label: CALENDAR_MONTHS[selectedMonthForExport], value: selectedMonthForExport }}
                    onChange={(e) => { const item = e.target.value; if (item != null) setSelectedMonthForExport(item.value); }}
                    style={{ width: '180px' }}
                  />
                  <Button
                    onClick={exportSingleMonthPDF}
                    style={{
                      padding: "10px 24px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Download PDF
                  </Button>
                </div>
                <div ref={singleMonthCalendarRef} style={{ backgroundColor: 'white', padding: '20px' }}>
                  {(() => {
                    const monthIndex = selectedMonthForExport;
                    const monthName = CALENDAR_MONTHS[monthIndex];
                    const monthNumber = monthIndex + 4;
                    const daysInMonth = moment(`2026-${monthNumber}`, 'YYYY-M').daysInMonth();
                    const firstDayOfWeek = moment(`2026-${monthNumber}-01`, 'YYYY-M-DD').day();
                    const monthEvents = eventList.filter(event => {
                      if (EVENT_IDS_EXCLUDED_FROM_STATS.includes(event.event_id as number)) return false;
                      if (!event.start_date) return false;
                      if (!approvedStatuses.includes(event.status as D4URxEventStatus)) return false;
                      const eventDate = moment(event.start_date);
                      return eventDate.year() === 2026 && eventDate.month() === monthNumber - 1;
                    });
                    const eventsByDay: { [key: number]: D4URxEvent[] } = {};
                    monthEvents.forEach(event => {
                      const day = moment(event.start_date).date();
                      if (!eventsByDay[day]) eventsByDay[day] = [];
                      eventsByDay[day].push(event);
                    });
                    const getEventLocation = (event: D4URxEvent) => {
                      const fmt = (city?: string, state?: string) => {
                        const c = city?.trim() || '';
                        const s = state?.trim() || '';
                        if (!c && !s) return '';
                        if (!c) return s;
                        if (!s) return c;
                        return `${c}, ${s}`;
                      };
                      try {
                        const location = event.event_custom_data ? JSON.parse(event.event_custom_data).event_location_type : '';
                        if (location === "The School" || location === "The Organization") {
                          return fmt(event.charity_city, event.charity_state) || fmt(event.city, event.state) || fmt(event.dealer_city, event.dealer_state);
                        } else if (location === "The Dealership") {
                          return fmt(event.dealer_city, event.dealer_state) || fmt(event.city, event.state) || fmt(event.charity_city, event.charity_state);
                        }
                        return fmt(event.city, event.state) || fmt(event.dealer_city, event.dealer_state) || fmt(event.charity_city, event.charity_state);
                      } catch {
                        return fmt(event.city, event.state) || fmt(event.dealer_city, event.dealer_state) || fmt(event.charity_city, event.charity_state);
                      }
                    };
                    return (
                      <div style={{ marginBottom: '40px' }}>
                        <h2 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>
                          {monthName} 2026
                        </h2>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          border: '2px solid #333',
                          tableLayout: 'fixed'
                        }}>
                          <thead>
                            <tr>
                              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                              <th key={day} style={{
                                border: '1px solid #666',
                                padding: '10px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                textAlign: 'center'
                              }}>
                                {day}
                              </th>
                            ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: Math.ceil((daysInMonth + firstDayOfWeek) / 7) }).map((_, weekIndex) => (
                              <tr key={weekIndex}>
                                {Array.from({ length: 7 }).map((_, dayIndex) => {
                                  const dayNumber = weekIndex * 7 + dayIndex - firstDayOfWeek + 1;
                                  const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
                                  const dayEvents = isValidDay ? eventsByDay[dayNumber] || [] : [];
                                  return (
                                    <td key={dayIndex} style={{
                                      border: '1px solid #999',
                                      padding: '8px',
                                      verticalAlign: 'top',
                                      height: '100px',
                                      backgroundColor: isValidDay ? 'white' : '#f0f0f0',
                                      position: 'relative'
                                    }}>
                                      {isValidDay && (
                                        <>
                                          <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '16px', color: '#333' }}>
                                            {dayNumber}
                                          </div>
                                          {dayEvents.map((event, idx) => (
                                            <div key={idx} style={{
                                              marginBottom: '4px',
                                              padding: '4px',
                                              backgroundColor: '#e3f2fd',
                                              borderLeft: '3px solid #007bff',
                                              fontSize: '11px',
                                              lineHeight: '1.3'
                                            }}>
                                              <div style={{ fontWeight: '600', color: '#000' }}>{event.event_name}</div>
                                              <div style={{ color: '#555' }}>{getEventLocation(event)}</div>
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <DialogActionsBar>
                <Button onClick={() => setShowIndividualMonthDialog(false)}>
                  Close
                </Button>
              </DialogActionsBar>
            </Dialog>
          )}

          <ExcelExport
            data={rdaData?.data}
            fileName={`IPSOS_events_export_${moment().format('YYYYMMDD-hhmma')}.xlsx`}
            ref={_exportRDA}
          >
            <ExcelExportColumn field="ffs_ford_campaign" title="Campaign ID" />
            <ExcelExportColumn field="ffs_ford_sequence" title="Sequence #" />
            <ExcelExportColumn field="ffs_event_id" title="Event ID" />
            <ExcelExportColumn field="start_date_export" title="Start Date" />
            <ExcelExportColumn field="end_date_export" title="End Date" />
            <ExcelExportColumn field="event_name" title="Event" />
            <ExcelExportColumn field="event_name" title="Event name for survey (i.e., consumer friendly)" />
            <ExcelExportColumn field="charity_partner" title="Event Partner Name (enter n/a if not applicable)" />
            <ExcelExportColumn field="address" title="Event Street Address" />
            <ExcelExportColumn field="city" title="Event City" />
            <ExcelExportColumn field="state" title="Event State" />
            <ExcelExportColumn field="zip_code" title="Event Zip Code" />

            <ExcelExportColumn field="market_area" title="Market" />
            <ExcelExportColumn field="audience_type" title="Audience Type" />
            <ExcelExportColumn field="category" title="Event Category" />
            <ExcelExportColumn field="language_option" title="Language Option" />
            <ExcelExportColumn field="attendance" title="Attendance" />
            <ExcelExportColumn field="indoor_outdoor" title="Event Indoor/Outdoor" />
            <ExcelExportColumn field="test_drive" title="Test Drive" />
            <ExcelExportColumn field="regional" title="Regional/National Program" />
            <ExcelExportColumn field="lincoln_event_partner" title="Lincoln Event Partner" />
            <ExcelExportColumn field="audience_type_full" title="Audience Type" />
            <ExcelExportColumn field="category_long" title="Event Category" />
            <ExcelExportColumn field="test_drive_full" title="Test Drive" />
            <ExcelExportColumn field="regional_full" title="Regional/National Program" />
            <ExcelExportColumn field="nat_charity" title="National Charity Partner" />
          </ExcelExport>
          <div style={{ marginBottom: '12px' }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Choose Excel columns ({selectedExportColumnDefs.length} selected)
              </summary>
              <div style={{ marginTop: '8px' }}>
                <button
                  className="k-button k-button-md k-rounded-md"
                  onClick={selectAllExportColumns}
                  type="button"
                  style={{ marginRight: '8px' }}
                >
                  Select All
                </button>
                <button
                  className="k-button k-button-md k-rounded-md"
                  onClick={clearAllExportColumns}
                  type="button"
                >
                  Clear All
                </button>
              </div>
              <div
                style={{
                  marginTop: '10px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))',
                  gap: '6px 12px',
                }}
              >
                {exportColumnDefs.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      checked={selectedExportColumns.includes(col.key)}
                      onChange={() => toggleExportColumn(col.key)}
                    />
                    <span>{col.title}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>

          <style>{`
            .events-grid-wrapper .k-grid-content { overflow-x: scroll; scrollbar-width: none; -ms-overflow-style: none; }
            .events-grid-wrapper .k-grid-content::-webkit-scrollbar { display: none; }
            .events-top-scrollbar { overflow-x: scroll; overflow-y: hidden; height: 14px; position: sticky; top: 0; z-index: 50; background: #f5f5f5; border-bottom: 1px solid #ddd; }
            .events-top-scrollbar::-webkit-scrollbar { height: 8px; }
            .events-top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
            .events-top-scrollbar::-webkit-scrollbar-thumb { background: #aaa; border-radius: 4px; }
            .events-top-scrollbar::-webkit-scrollbar-thumb:hover { background: #666; }
          `}</style>
          <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <div ref={topScrollbarRef} className="events-top-scrollbar" onScroll={(e) => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) gc.scrollLeft = e.currentTarget.scrollLeft; }}>
              <div style={{ width: scrollbarWidth, height: 1 }} />
            </div>
            <div ref={gridContentRef} className="events-grid-wrapper">
          <Grid
            data={dataResult}
            rowRender={rowRender}
            sortable={{ mode: "multiple" }}
            {...dataState}
            onDataStateChange={(event: GridDataStateChangeEvent) => {
              console.log("event.dataState", event.dataState);
              setDataResult(customProcess(eventList, event.dataState));
              setDataState(event.dataState);
            }}
            resizable={true}
            onColumnResize={() => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) setScrollbarWidth(gc.scrollWidth); }}
          >

            <GridToolbar>
              Quick Views:
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.default)}>Default</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.action)}>Action Needed</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.waiting)}>Waiting on Dealer</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.today)}>Today's Events</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.tomorrow)}>Tomorrow</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.thisWeek)}>This Week</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.nextWeek)}>Next Week</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.yesterday)}>Yesterday</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.lastWeek)}>Last Week</button>
              {/* <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.blueCruise)}>Blue Cruise</button> */}
              {/* <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.clear)}>Clear All</button> */}
              {/* <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={excelExport}>Export</button> */}
              {/* <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={excelExportRDA}>IPSOS Export</button> */}
            </GridToolbar>
            <GridToolbar>
              <button className="k-button k-button-md k-rounded-md " onClick={() => doDataState(sortPresets.clear)}>Clear All</button>
              <button
                className="k-button k-button-md k-rounded-md"
                onClick={excelExport}
                disabled={selectedExportColumnDefs.length === 0}
              >
                Export ({selectedExportColumnDefs.length} columns)
              </button>
            </GridToolbar>
            {/* <GridColumn
              field="event_name"
              title="Event Name"
              columnMenu={ColumnMenu}
            /> */}
            <GridColumn
              field="dealer_name"
              title={localizationService.toLanguageString(
                "custom.dealer",
                "dealer"
              )}
              columnMenu={ColumnMenu}
              cell={DealerNameCell}
              width={200}
            />
            {/* <GridColumn cell={BlueCruiseCell} width={250} /> */}
            <GridColumn
              field="charity_partner"
              title="Organization"
              columnMenu={ColumnMenu}
              width={200}
            />
            {/* <GridColumn
              title="Planner"
              cell={plannersCell}
              columnMenu={ColumnMenu}
            /> */}
            <GridColumn
              field="sort_date"
              title="Event Date"
              format="{0:E, MMM dd yyyy}"
              filter="date"
              columnMenu={ColumnMenu}
              width={200}
            />
            <GridColumn
              field="firstTimeDealerGridCell"
              title="1st Time Dealer"
              columnMenu={ColumnMenu}
              width={200}
            />
            <GridColumn
              field="hispanicReadable"
              title="Hispanic"
              columnMenu={ColumnMenu}
              width={200}
            />
            {/* Update the outreach column */}
            <GridColumn 
              cell={OutreachCell} 
              field="outreach_status_filter"
              title="Outreach"
              columnMenu={ColumnMenuCheckboxFilter(eventList, {
                searchBox: () => null,
              })}
              width={200} 
            />

            <GridColumn
              field="status_pretty"
              title="Status"
              columnMenu={ColumnMenuCheckboxFilter(eventList, {
                searchBox: () => null,
              })}
              width={200}
            />
            <GridColumn
              cell={TrainingStatusCell}
              field="training_status_pretty"
              title="Training Status"
              columnMenu={ColumnMenuCheckboxFilter(eventList, {
                searchBox: () => null,
              })}
              width={200}
            />
            <GridColumn
              field="check_amount_pretty"
              title="Check"
              width={100}
            />

            <GridColumn cell={EventActionsCell} width={200} />
          </Grid>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(EventsScreen);
